import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { gameOgImage } from "@/lib/og-images";
import { GameHistoryView } from "@/components/GameHistoryView";
import { breadcrumbSchema } from "@/lib/jsonld";
import { canonicalUrl } from "@/lib/seo-policy";
import { latestDrawSummary } from "@/lib/results-nav";

const searchSchema = z.object({ page: fallback(z.number().int(), 1).default(1) });

export const Route = createFileRoute("/$stateSlug/$gameSlug/history")({
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  head: ({ params, match }) => {
    const name = humanizeSlug(params.gameSlug);
    const state = humanizeSlug(params.stateSlug);
    const page = Math.max(1, Math.floor((match as { search?: { page?: number } } | undefined)?.search?.page ?? 1));
    const basePath = `/${params.stateSlug}/${params.gameSlug}/history`;
    const path = page > 1 ? `${basePath}?page=${page}` : basePath;
    const title = page > 1
      ? `${state} ${name} Past Results — Page ${page}`
      : `${state} ${name} Past Winning Numbers Archive`;
    const description = page > 1
      ? `Older ${state} ${name} winning numbers — page ${page} of the drawing archive.`
      : `Complete ${state} ${name} drawing history: past winning numbers, bonus balls and draw dates.`;
    const latest = latestDrawSummary(params.gameSlug, params.stateSlug);
    // "Lotto Texas" already carries the state — don't emit "Texas Lotto Texas".
    const scope = name.toLowerCase().includes(state.toLowerCase()) ? name : `${state} ${name}`;
    const socialTitle = latest
      ? `${scope} winning numbers — latest draw ${latest.dateLabel}`
      : `${scope} past winning numbers`;
    const socialDesc = latest && latest.numbersText
      ? `Most recent ${scope} draw (${latest.dateLabel}): ${latest.numbersText}. Browse every past drawing by date.`
      : description;
    const seo = buildSeo({
      title,
      description,
      path,
      image: gameOgImage(params.gameSlug, params.stateSlug),
      jsonLd: [
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name: state, item: `/states/${params.stateSlug}` },
          { name, item: `/${params.stateSlug}/${params.gameSlug}` },
          { name: "History", item: basePath },
        ]),
      ],
    });
    const prevPath = page > 2 ? `${basePath}?page=${page - 1}` : basePath;
    return {
      ...seo,
      meta: [
        ...seo.meta,
        { property: "og:title", content: socialTitle },
        { property: "og:description", content: socialDesc },
        { name: "twitter:title", content: socialTitle },
        { name: "twitter:description", content: socialDesc },
        ...(latest ? [
          { name: "twitter:label1", content: "Latest draw" },
          { name: "twitter:data1", content: latest.dateLabel },
          { name: "twitter:label2", content: "Winning numbers" },
          { name: "twitter:data2", content: latest.numbersText || "See page" },
        ] : []),
      ],
      links: [
        ...seo.links,
        ...(page > 1 ? [{ rel: "prev", href: `${canonicalUrl(basePath)}${prevPath === basePath ? "" : `?page=${page - 1}`}` }] : []),
      ],
    };
  },
  component: StateGameHistoryRoute,
});

function StateGameHistoryRoute() {
  const { stateSlug, gameSlug } = Route.useParams();
  const { page } = Route.useSearch();
  return <GameHistoryView gameSlug={gameSlug} stateSlug={stateSlug} page={page ?? 1} />;
}
